import * as React from "react";
import { Outlet } from "react-router";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Toolbar from "@mui/material/Toolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAccount, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { AlertCommon } from "../AlertCommon";
import { SignInButton } from "../SignInButton";
import { SignOutButton } from "../SignOutButton";
import DashboardHeader from "./DashboardHeader";
import DashboardSidebar from "./DashboardSidebar";

const mobileLogo = "/images/simplified/coc-logo-simplify.svg";
const desktopLogo = "/images/coc-logo.svg";

interface DashboardLayoutProps {
  fixedHeader?: boolean;
  children?: React.ReactNode;
}

export default function DashboardLayout({
  fixedHeader = false,
  children,
}: DashboardLayoutProps) {
  const theme = useTheme();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();

  const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] =
    React.useState(false);
  const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] =
    React.useState(false);

  const isOverMdViewport = useMediaQuery(theme.breakpoints.up("md"));

  const isNavigationExpanded = isOverMdViewport
    ? isDesktopNavigationExpanded
    : isMobileNavigationExpanded;

  const setIsNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => {
      if (isOverMdViewport) {
        setIsDesktopNavigationExpanded(newExpanded);
      } else {
        setIsMobileNavigationExpanded(newExpanded);
      }
    },
    [
      isOverMdViewport,
      setIsDesktopNavigationExpanded,
      setIsMobileNavigationExpanded,
    ],
  );

  const handleToggleHeaderMenu = React.useCallback(
    (isExpanded: boolean) => {
      setIsNavigationExpanded(isExpanded);
    },
    [setIsNavigationExpanded],
  );

  const layoutRef = React.useRef<HTMLDivElement>(null);
  const [containerElement, setContainerElement] = React.useState<
    HTMLElement | undefined
  >(undefined);

  React.useEffect(() => {
    setContainerElement(layoutRef.current ?? undefined);
  }, []);

  const accountDisplayName = account?.username?.split("@")[0] ?? "";
  const envName = process.env.REACT_APP_ENV;
  const appName = process.env.REACT_APP_NAME;
  const shortAppName = process.env.REACT_APP_SHORT_NAME;
  const authControl = isAuthenticated ? <SignOutButton /> : <SignInButton />;

  return (
    <Box
      ref={layoutRef}
      sx={{
        position: "relative",
        display: "flex",
        overflow: fixedHeader ? "hidden" : "visible",
        overflowY: fixedHeader ? "hidden" : "auto",
        overflowX: "hidden",
        flexGrow: 1, // allows footer to stick to bottom
        height: "auto",
        width: "100%",
        maxWidth: "100vw",
        ...(fixedHeader ? {} : { flexDirection: "column" }),
      }}
    >
      <DashboardHeader
        menuOpen={isNavigationExpanded}
        onToggleMenu={handleToggleHeaderMenu}
        isAuthenticated={isAuthenticated}
        accountDisplayName={accountDisplayName}
        envName={envName}
        appName={appName}
        shortAppName={shortAppName}
        mobileLogoSrc={mobileLogo}
        desktopLogoSrc={desktopLogo}
        authControl={authControl}
        fixedHeader={fixedHeader}
      />
      {fixedHeader ? (
        <>
          {isAuthenticated ? (
            <DashboardSidebar
              expanded={isNavigationExpanded}
              setExpanded={setIsNavigationExpanded}
              container={containerElement}
              isAuthenticated={isAuthenticated}
            />
          ) : null}
          {/* Main content area grows to fill available space */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Toolbar sx={{ displayPrint: "none" }} />
            <Box
              component="main"
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Container
                maxWidth="xl"
                sx={{
                  mt: 4,
                  mb: 4,
                }}
              >
                <AlertCommon id="default-alert" />
                {children ?? <Outlet />}
              </Container>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flex: 1,
          }}
        >
          {isAuthenticated ? (
            <DashboardSidebar
              expanded={isNavigationExpanded}
              setExpanded={setIsNavigationExpanded}
              container={containerElement}
              isAuthenticated={isAuthenticated}
            />
          ) : null}
          <Box
            component="main"
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor: (theme) =>
                theme.palette.mode === "light"
                  ? theme.palette.grey[100]
                  : theme.palette.grey[900],
            }}
          >
            <Container
              maxWidth="xl"
              sx={{
                mt: 4,
                mb: 4,
              }}
            >
              <AlertCommon id="default-alert" />
              {children ?? <Outlet />}
            </Container>
          </Box>
        </Box>
      )}
    </Box>
  );
}
